import type { V2Fail, V2Success } from "@branding/config";

export type FieldError = { field: string; message: string };

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string;
  readonly errors: FieldError[];
  readonly title: string | null;
  readonly description: string | null;

  constructor(input: {
    status: number;
    code: string;
    traceId?: string;
    errors?: FieldError[];
    title?: string | null;
    description?: string | null;
  }) {
    super(input.title ?? input.description ?? input.code);
    this.name = "ApiClientError";
    this.status = input.status;
    this.code = input.code;
    this.traceId = input.traceId ?? "";
    this.errors = input.errors ?? [];
    this.title = input.title ?? null;
    this.description = input.description ?? null;
  }

  get unauthorized(): boolean {
    return this.status === 401 || this.code.endsWith("__UNAUTHORIZED");
  }
}

function isEnvelope(value: unknown): value is V2Success<unknown> | V2Fail {
  return Boolean(
    value &&
      typeof value === "object" &&
      "metadata" in value &&
      value.metadata &&
      typeof value.metadata === "object" &&
      "code" in value.metadata,
  );
}

export function mapV2<T>(payload: unknown, status: number): T {
  if (!isEnvelope(payload)) {
    throw new ApiClientError({
      status,
      code: "API_MARKET__INTERNAL_ERROR",
      title: "Respons tidak dikenali",
      description: "Server tidak mengirim envelope V2.",
    });
  }

  const errors = payload.metadata.errors ?? [];
  const failed =
    status >= 400 ||
    payload.data == null ||
    errors.length > 0 ||
    !payload.metadata.code.endsWith("__SUCCESS");

  if (failed) {
    throw new ApiClientError({
      status,
      code: payload.metadata.code,
      traceId: payload.metadata.trace_id,
      errors,
      title: payload.metadata.message_title,
      description: payload.metadata.message_description,
    });
  }

  return payload.data as T;
}

export function fieldErrorMap(errors: FieldError[]): Record<string, string> {
  return Object.fromEntries(errors.map((error) => [error.field, error.message]));
}
